<template>
    <v-container class="pa-5" fluid>
        <v-dialog
            v-model="previewDialog"
            scrollable
            max-width="600px"
            eager
            @keydown.esc="closePreviewDialog"
            @click:outside="closePreviewDialog"
        >
            <v-card tile>
                <v-card-text>
                    <img ref="imagePreview" class="image-preview" alt="Preview">
                </v-card-text>
                <v-card-actions>
                    <v-btn data-sentry-label="Zamknij" text color="info" @click="closePreviewDialog">
                        Zamknij
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="attachmentsDialog"
            scrollable
            max-width="600px"
            eager
            @keydown.esc="attachmentsDialog = false"
            @click:outside="attachmentsDialog = false"
        >
            <v-card tile>
                <v-card-title>
                    Lieferschein
                </v-card-title>
                <v-card-text>
                    <v-simple-table>
                        <template #default>
                            <tbody>
                                <tr
                                    v-for="(file, index) in order.attachments"
                                    :key="file + index"
                                >
                                    <td><a :href="file" target="_blank">{{ parseFileName(file) }}</a></td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>
                </v-card-text>
                <v-card-actions>
                    <v-btn data-sentry-label="Zamknij" text color="info" @click="attachmentsDialog = false">
                        Zamknij
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <DeliveryDialog
            :is-open="editDeliveryDialog"
            :delivery-data="currentDelivery"
            :order-quantity="order.quantity"
            :order-deliveries="order.deliveries"
            :action-loading="actionLoading.saveDelivery"
            @on-save="updateDelivery"
            @on-close="closeEditDeliveryDialog"
        />

        <page-title>
            Zamówienie {{ order.shopOrderNumber }}
            <span :class="statusClass" class="text-uppercase text-body-2">
                {{ orderStatus }}
            </span>
        </page-title>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container>
                    <v-row justify="space-between" align="center">
                        <v-col sm="10">
                            <v-btn
                                :disabled="!canBeSubmitted"
                                class="mb-4 mr-4 white--text"
                                data-sentry-label="Złóż zamówienie"
                                color="success"
                                :loading="submitOrderLoading"
                                @click="submitOrder"
                            >
                                Złóż zamówienie
                            </v-btn>
                            <v-btn
                                :disabled="order.statusId !== 15 && order.statusId !== 110"
                                class="mb-4 mr-4 white--text"
                                data-sentry-label="Wstrzymaj zamówienie"
                                color="info"
                                :loading="suspendOrderLoading"
                                @click="suspendHandler"
                            >
                                {{ suspendButtonText }}
                            </v-btn>
                            <v-btn
                                :disabled="!canBeCanceled"
                                color="pink"
                                class="mb-4 mr-4 white--text"
                                data-sentry-label="Anuluj zamówienie"
                                :loading="cancelOrderLoading"
                                @click="cancelOrderHandler"
                            >
                                <v-icon left>
                                    mdi-file-remove-outline
                                </v-icon>
                                Anuluj zamówienie
                            </v-btn>
                            <v-btn
                                :disabled="!newOrder"
                                color="pink"
                                class="mb-4 mr-4 white--text"
                                data-sentry-label="Usuń zamówienie"
                                :loading="removeOrderLoading"
                                @click="removeOrderHandler"
                            >
                                <v-icon left>
                                    mdi-delete
                                </v-icon>
                                Usuń zamówienie
                            </v-btn>
                            <v-btn
                                :disabled="!newOrder"
                                color="#F57C00"
                                class="mb-4 mr-4 white--text"
                                data-sentry-label="Zamówienie reklamacyjne"
                                :loading="setOrderToComplaintOrderLoading"
                                @click="setOrderAsComplaintOrderHandler"
                            >
                                Zamówienie reklamacyjne
                            </v-btn>
                            <v-btn
                                :disabled="!newOrder || isPriority"
                                color="info"
                                class="mb-4 mr-4 white--text"
                                data-sentry-label="Priorytet"
                                @click="setAsPriority"
                            >
                                Priorytet
                            </v-btn>
                            <v-btn
                                :disabled="!isOrderApproved"
                                color="info"
                                class="mb-4 white--text"
                                data-sentry-label="Zgłoś reklamacje"
                                @click="createComplaint"
                            >
                                Zgłoś reklamację
                            </v-btn>
                        </v-col>
                        <div sm="2" justify="end">
                            <v-btn
                                class="mb-4 mr-4 white--text"
                                data-sentry-label="Lieferschein"
                                color="info"
                                :disabled="!hasAttachments"
                                @click="openAttachments()"
                            >
                                Lieferschein
                            </v-btn>
                        </div>
                    </v-row>
                    <v-row>
                        <v-col cols="auto">
                            <h2 class="text-h6">
                                Parametry zamówienia
                            </h2>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card>
                                <v-card-text>
                                    <v-container class="pa-0">
                                        <v-row>
                                            <v-col v-for="(param, index) in shopParameters" :key="'shopParameters' + index" cols="12" sm="4">
                                                <v-menu
                                                    v-if="param.type === 'date'"
                                                    v-model="menuTo[param.key]"
                                                    :close-on-content-click="false"
                                                    transition="scale-transition"
                                                    bottom
                                                    nudge-bottom="40"
                                                    nudge-right="34"
                                                    min-width="290px"
                                                >
                                                    <template #activator="{on}">
                                                        <v-text-field
                                                            :value="param.value"
                                                            :label="param.title"
                                                            :disabled="param.disabled !== false"
                                                            readonly
                                                            outlined
                                                            dense
                                                            v-on="on"
                                                        />
                                                    </template>
                                                    <v-date-picker
                                                        no-title
                                                        scrollable
                                                        first-day-of-week="1"
                                                        :min="new Date().toISOString()"
                                                        @input="dateHandler($event, param.key)"
                                                    />
                                                </v-menu>

                                                <v-text-field
                                                    v-else
                                                    class="mb-1"
                                                    :value="param.value"
                                                    :label="param.title"
                                                    dense
                                                    outlined
                                                    :readonly="param.readonly === true"
                                                    hide-details
                                                    :disabled="param.disabled !== false || !canUpdates"
                                                />
                                            </v-col>
                                            <v-col v-for="(parameter, index) in productParameters" :key="'productParameters' + index" cols="12" sm="4">
                                                <v-text-field
                                                    class="mb-1"
                                                    :value="parameter.value"
                                                    :label="parameter.title"
                                                    dense
                                                    disabled
                                                    outlined
                                                    hide-details
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Adresy do wysyłki
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-data-table
                                    v-model="labelsSelectedToPrint"
                                    :headers="orderAddressesHeaders"
                                    :items="order.deliveries"
                                    :loading="actionLoading.saveOrder"
                                    item-key="id"
                                    hide-default-footer
                                    disable-pagination
                                    :mobile-breakpoint="0"
                                    name="orderAddressesData"
                                >
                                    <template #item.address="{item}">
                                        {{ item.companyName }}, {{ item.firstName }} {{ item.lastName }}, {{ item.street }}, {{ item.zipCode }} {{ item.city }}
                                    </template>
                                    <template #item.quantity="{item}">
                                        {{ item.quantity }} szt.
                                    </template>
                                    <template #item.patterns="{item}">
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <span v-on="on">{{ item.patterns.length }}</span>
                                            </template>
                                            <span>
                                                <span v-for="(pattern, index) in item.patterns" :key="index">
                                                    Wzór {{ pattern.pattern }} <br>
                                                </span>
                                            </span>
                                        </v-tooltip>
                                    </template>
                                    <template #item.weight="{item}">
                                        {{ formatWeight(item.weight) }}
                                    </template>
                                    <template #item.parcels="{item}">
                                        {{ item.parcels.length }}
                                    </template>
                                    <template #item.status="{item}">
                                        {{ deliveryStatusName(item.status) }}
                                    </template>
                                    <template #item.manually="{item}">
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    text
                                                    icon
                                                    color="primary"
                                                    data-sentry-label="Edycja"
                                                    :disabled="item.status !== 10"
                                                    v-on="on"
                                                    @click="showEditDeliveryDialog(item)"
                                                >
                                                    <v-icon small>
                                                        mdi-pencil
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Edytuj</span>
                                        </v-tooltip>
                                    </template>
                                </v-data-table>

                                <v-divider />
                                <v-card-actions>
                                    <v-btn
                                        :disabled="!canGeneratePrint"
                                        :loading="actionLoading.print"
                                        color="info"
                                        data-sentry-label="Generuj etykietę"
                                        outlined
                                    >
                                        Generuj etykietę
                                        <v-icon right dark>
                                            mdi-printer
                                        </v-icon>
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container v-if="hasFilePreviews">
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Podgląd plików
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card class="preview">
                                <v-progress-linear
                                    color="blue"
                                    :indeterminate="actionLoading.preview"
                                    height="2"
                                />
                                <v-divider />
                                <v-card-text>
                                    <div>
                                        <template>
                                            <a
                                                v-for="preview in previews"
                                                :key="preview.id"
                                                @click="openPreviewDialog(preview.previewUrl)"
                                            >
                                                <img
                                                    :src="preview.previewUrl"
                                                    class="thumbnail-preview"
                                                    alt=""
                                                >
                                            </a>
                                        </template>
                                    </div>
                                </v-card-text>
                                <v-divider />
                                <v-card-actions>
                                    <v-menu offset-y>
                                        <template #activator="{on}">
                                            <v-btn
                                                color="primary"
                                                text
                                                data-sentry-label="Pobranie plików produkcyjnych WMD"
                                                :disabled="!hasProductionFiles"
                                                v-on="on"
                                            >
                                                Pliki produkcyjne
                                                <v-icon right dark>
                                                    mdi-download
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list dense>
                                            <v-list-item
                                                v-for="(orderProduct, index) in order.orderProducts"
                                                :key="index"
                                                :href="orderProduct.productionFileUrl"
                                            >
                                                <a :href="orderProduct.productionFileUrl" target="_blank">
                                                    <v-list-item-title>
                                                        {{ orderProduct.productionFileUrl }}
                                                    </v-list-item-title>
                                                </a>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>

                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <h2 class="text-h6">
                                Komentarze do zamówienia
                            </h2>
                        </v-col>
                        <v-col cols="12">
                            <v-card>
                                <v-list v-if="hasComments" two-line>
                                    <template v-for="(comment, index) in order.comments">
                                        <v-list-item
                                            :key="comment.createdAt"
                                        >
                                            <v-list-item-avatar>
                                                <v-avatar
                                                    size="40px"
                                                    color="blue-grey"
                                                >
                                                    <span class="white--text text-uppercase">
                                                        {{ parseUserInitials(comment.username) }}
                                                    </span>
                                                </v-avatar>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    {{ comment.comment }}
                                                </v-list-item-title>
                                                <v-list-item-subtitle>
                                                    {{ comment.username }} - {{ formatDate(comment.createdAt) }}
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                        <v-divider :key="index" />
                                    </template>
                                </v-list>
                                <v-card-text v-else>
                                    <v-alert
                                        type="info"
                                        text
                                        border="left"
                                    >
                                        Brak komentarzy do zamówienia.
                                    </v-alert>
                                </v-card-text>

                                <v-card-text>
                                    <v-textarea
                                        v-model="newComment"
                                        label="Dodaj komentarz"
                                        :disabled="actionLoading.addComment || !canAddComment"
                                        auto-grow
                                        clearable
                                        hide-details
                                    />
                                    <v-row justify="end">
                                        <v-col>
                                            <v-btn
                                                color="primary"
                                                data-sentry-label="Wyślij komentarz"
                                                :disabled="newComment === null || newComment.length <= 0"
                                                :loading="actionLoading.addComment"
                                                @click="addComment"
                                            >
                                                Wyślij
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import DeliveryDialog from './../Common/DeliveryDialog.vue'

import dateFormatter from '@/mixins/dateFormatter'
import parse from '@/mixins/parse'
import priceFormatter from '@/mixins/priceFormatter'
import tooltipDelay from '@/mixins/tooltip'
import weightFormatter from '@/mixins/weightFormatter'
import { required } from 'vuelidate/lib/validators'

import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'WMDOrder',
    components: {
        PageTitle,
        DeliveryDialog
    },
    mixins: [
        dateFormatter,
        parse,
        priceFormatter,
        tooltipDelay,
        weightFormatter
    ],
    beforeRouteEnter(to, from, next) {
        store.dispatch('orders/getWMDDeliveryStatusNames')
            .then(() => {
                store.dispatch('orders/getWMDOrder', to.params.id)
                    .then(response => {
                        next(vm => {
                            vm.order = response.data
                        })
                    })
                    .catch(error => {
                        next(error)
                        errorHandler(error)
                    })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            order: {
                status: {
                    name: null
                },
                plannedSendDate: null,
                attachments: [],
                orderProducts: [{
                    productionFileUrl: null,
                    previews: [{
                        previewUrl: null
                    }]
                }],
                overdue: false,
                isComplaint: false
            },
            menuTo: [],
            submitOrderLoading: false,
            suspendOrderLoading: false,
            cancelOrderLoading: false,
            removeOrderLoading: false,
            setOrderToComplaintOrderLoading: false,
            attachmentsDialog: false,
            previewDialog: false,
            labelsSelectedToPrint: [],
            currentDelivery: {},
            parcelsDialog: false,
            sentManuallyDialog: false,
            newComment: '',
            editDeliveryDialog: false,
            actionLoading: {
                preview: false,
                print: false,
                addComment: false,
                saveOrder: false,
                saveDelivery: false
            },
            orderAddressesHeaders: [
                {
                    text: 'Adres',
                    align: 'left',
                    sortable: false,
                    value: 'address'
                },
                {
                    text: 'Nakład',
                    align: 'center',
                    sortable: false,
                    value: 'quantity'
                },
                {
                    text: 'Ilość wzorów',
                    align: 'center',
                    sortable: false,
                    value: 'patterns'
                },
                {
                    text: 'Waga przesyłki',
                    align: 'center',
                    sortable: false,
                    value: 'weight'
                },
                {
                    text: 'Ilość kartonów',
                    align: 'center',
                    sortable: false,
                    value: 'parcels'
                },
                {
                    text: 'Status',
                    align: 'center',
                    sortable: false,
                    value: 'status',
                    width: 140
                },
                {
                    text: 'Akcje',
                    align: 'left',
                    sortable: false,
                    value: 'manually'
                }
            ]
        }
    },
    validations() {
        const validations = {
            order: {}
        }

        if (this.canUpdatesPlannedSendDate) {
            validations.order.plannedSendDate = {
                required
            }
        }

        return validations
    },
    computed: {
        suspendButtonText() {
            return this.order.statusId !== 110 ? 'Wstrzymaj zamówienie' : 'Wznów zamówienie' // 110 = wstrzymane
        },
        canGeneratePrint() {
            return false
        },
        hasComments() {
            return this.order.comments ? this.order.comments.length > 0 : false
        },
        hasFilePreviews() {
            return !!this.order.orderProducts?.[0]?.previews
        },
        hasAttachments() {
            return !!this.order.attachments?.length
        },
        canUpdates() {
            return ![13, 15, 120, 130].includes(this.order.statusId)
        },
        canBeSubmitted() {
            return this.order.statusId === 10
        },
        canBeCanceled() {
            return this.order.statusId === 15
        },
        isOrderApproved() {
            return this.order.statusId === 15
        },
        isPriority() {
            return this.order.isPriority
        },
        canAddComment() {
            return ![13, 130].includes(this.order.statusId)
        },
        newOrder() {
            return this.order.statusId === 10
        },
        canUpdatesPlannedSendDate() {
            return ![13, 110, 120, 130].includes(this.order.statusId)
        },
        hasUpdates() {
            return this.$v.$dirty && !this.$v.$invalid
        },
        previews() {
            return this.order.orderProducts?.[0]?.previews
        },
        hasProductionFiles() {
            return !!this.order.orderProducts?.[0]?.productionFileUrl
        },
        deliveryStatusNames() {
            return this.$store.state.orders.wmdOrder.wmdDeliveryStatusNames
        },
        orderStatus() {
            return this.order.overdue ? 'opóźnione' : this.order.status.name
        },
        statusClass() {
            if (this.order.overdue) {
                return 'warning--text'
            }

            if ([10, 15].includes(this.order.statusId))
            {
                return 'info--text'
            }
            if (this.order.statusId === 50) {
                return 'success--text'
            }
            if (this.order.statusId === 120) {
                return 'error--text'
            }

            return ''
        },
        shopParameters() {
            return [
                {
                    title: 'Numer klienta',
                    key: 'shopOrderNumber',
                    value: this.order.shopOrderNumber,
                    disabled: false,
                    readonly: true,
                },
                {
                    title: 'Numer zamówienia',
                    key: 'fullProductionNumber',
                    value: this.order.fullProductionNumber,
                    disabled: false,
                    readonly: true,
                },
                {
                    title: 'Nazwa sklepu',
                    key: 'shopName',
                    value: this.order.shopName,
                },
                {
                    title: 'Data zamówienia',
                    key: 'createdAt',
                    value: this.formatDate(this.order.createdAt),
                },
                {
                    title: 'Planowana data wysyłki',
                    key: 'plannedSendDate',
                    value: this.formatDate(this.order.plannedSendDate, false),
                    type: 'date',
                    disabled: !this.canUpdatesPlannedSendDate,
                },
                {
                    title: 'Data dostawy',
                    key: 'plannedDeliveryDate',
                    type: 'date',
                    value: this.formatDate(this.order.plannedDeliveryDate, false),
                },
                {
                    title: 'Produkt',
                    key: 'shopProductName',
                    value: this.order.shopProductName,
                },
                {
                    title: 'Nakład',
                    key: 'numberOfTemplates',
                    value: `${this.order.numberOfTemplates} x ${this.order.quantity} szt.`,
                }
            ]
        },
        productParameters() {
            const params = []
            const exclude = ['template', 'bleed', 'package_value', 'package_width', 'package_height', 'package_depth', 'format_y', 'format_x']

            if (this.order.parameters) {
                this.order.parameters.forEach(parameter => {
                    if (!exclude.includes(parameter.parameterName)) {
                        params.push({
                            title: parameter.parameterName,
                            value: parameter.parameterValueName
                        })
                    }
                })
            }

            return params
        },
        userName() {
            return this.$store.state.user.userName
        },
    },
    methods: {
        saveOrder(key) {
            if (!this.hasUpdates) return

            this.$store.dispatch('orders/updateWMDOrder', {
                id: this.order.id,
                [key]: this.order[key]
            })
                .then(response => {
                    this.order = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        dateHandler(value, key) {
            this.order[key] = value + 'T00:00'
            this.menuTo[key] = false
            this.$v.order[key].$touch()
            this.saveOrder(key)
        },
        submitOrder() {
            this.submitOrderLoading = true
            this.$store.dispatch('orders/acceptedWMDOrder', this.order.id)
                .then(response => {
                    this.order = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.submitOrderLoading = false
                })
        },
        addComment() {
            this.actionLoading.addComment = true

            this.$store.dispatch('orders/addCommentWMD', {
                comment: this.newComment,
                username: this.userName,
                orderId: this.order.id
            })
                .then(response => {
                    this.order.comments.push(response.data)
                    this.newComment = ''
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.addComment = false
                })
        },
        suspendHandler() {
            this.suspendOrderLoading = true
            this.$store.dispatch('orders/updateWMDOrder', {
                id: this.order.id,
                suspend: this.order.statusId !== 110,
            })
                .then(response => {
                    this.order = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.suspendOrderLoading = false
                })
        },
        cancelOrderHandler() {
            this.cancelOrderLoading = true
            this.$store.dispatch('orders/canceledWMDOrder', this.order.id)
                .then(response => {
                    this.order = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.cancelOrderLoading = false
                })
        },
        removeOrderHandler() {
            this.removeOrderLoading = true
            this.$store.dispatch('orders/removeWMDOrder', this.order.id)
                .then(response => {
                    this.order = response.data
                })
                .catch(error => errorHandler(error))
                .finally(() => {
                    this.removeOrderLoading = false
                })
        },
        setOrderAsComplaintOrderHandler() {
            this.setOrderToComplaintOrderLoading = true
            this.$store.dispatch('orders/updateWMDOrder', {
                id: this.order.id,
                isComplaint: true,
            })
                .then(response => {
                    this.order = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.setOrderToComplaintOrderLoading = false
                })
        },
        setAsPriority() {
            this.order.isPriority = true
            this.$v.order.$touch()
            this.saveOrder('isPriority')
        },
        getOrder() {
            this.$store.dispatch('orders/getWMDOrder', this.order.id)
                .then(response => {
                    this.order = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        deliveryStatusName(statusId) {
            return this.deliveryStatusNames ? this.deliveryStatusNames.find(item => item.id === statusId).name : statusId
        },
        close() {
            this.$router.push({ name: 'ordersWMDOrders' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createComplaint() {
            this.$router.push({
                name: 'complaintsComplaintsCreate',
                params: { complaintType: 2, fullProductionNumber: this.order.fullProductionNumber }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openAttachments() {
            if (this.order.attachments.length > 1) {
                this.attachmentsDialog = true

                return
            }
            window.open(this.order.attachments.at(0), '_blank')
        },
        openPreviewDialog(imageURI) {
            const image = this.$refs.imagePreview

            this.actionLoading.preview = true

            image.setAttribute('src', imageURI)
            image.onload = () => {
                this.previewDialog = true
                this.actionLoading.preview = false
            }
            image.onerror = () => {
                this.actionLoading.preview = false
            }
        },
        closePreviewDialog() {
            this.previewDialog = false
        },
        showEditDeliveryDialog(item) {
            this.editDeliveryDialog = true
            this.currentDelivery = item
        },
        updateDelivery(payload) {
            this.actionLoading.saveDelivery = true
            this.$store.dispatch('orders/updateWMDDelivery', payload)
                .then(() => {
                    this.getOrder()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.saveDelivery = false
                })
        },
        closeEditDeliveryDialog() {
            this.editDeliveryDialog = false
            setTimeout(() => {
                this.currentDelivery = {}
            }, 500)
        },
    }
}
</script>